import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  LayoutDashboard,
  LogOut,
  Settings as SettingsIcon,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Monitors' },
  { to: '/admin/incidents', icon: AlertTriangle, label: 'Incidents' },
  { to: '/admin/settings', icon: SettingsIcon, label: 'Settings' },
];

interface Me {
  email: string;
  isAdmin: boolean;
  via: 'access' | 'session';
}

export function Layout() {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    api
      .me()
      .then((r) => setMe(r.user))
      .catch(() => setMe(null));
  }, []);

  async function logout() {
    // Clear the password-login cookie if that's how we got in.
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    // Cloudflare Access sign-out URL — ends the Access session too.
    // Falls back to /login if Access isn't in front.
    location.href = '/cdn-cgi/access/logout';
  }

  return (
    <div className="min-h-full grid grid-cols-[220px_1fr]">
      <aside className="border-r bg-card px-4 py-6 flex flex-col">
        <a
          href="/"
          className="flex items-center gap-2 px-2 mb-8 text-foreground hover:text-primary transition-colors"
          title="Back to public status page"
        >
          <Activity className="h-5 w-5 text-green-500" />
          <span className="font-semibold tracking-tight">PX7 Uptime</span>
        </a>

        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/admin'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        {me && (
          <div className="mt-auto pt-6 border-t">
            <div
              className="px-3 py-2 text-xs text-muted-foreground break-all"
              title={`Signed in via ${me.via}`}
            >
              Signed in as
              <div className="mt-0.5 font-mono text-[11px] text-foreground">
                {me.email}
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        )}
      </aside>
      <main className="p-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
